// lib/takeoff/workingDrawing.js
// Working drawings as one real PDF: every sheet on its own page at its own paper size, the drawing as a picture,
// the takeoff and the legend drawn over it as vector lines and text. Download it, print it or share it.
import { PDFDocument, StandardFonts, rgb, LineCapStyle } from "pdf-lib";

const U = 72; // PDF points per inch
const WHITE = rgb(1, 1, 1);
const INK = rgb(0.07, 0.07, 0.07);
const r2 = (v) => Math.round(v * 100) / 100;

// the standard PDF fonts only know Latin-1 + a few extras — anything else becomes "?"
const WIN = new Set([..."€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ"]);
const safe = (s) =>
  String(s ?? "")
    .replace(/′/g, "'")
    .replace(/″/g, '"')
    .replace(/[\r\n\t]+/g, " ")
    .split("")
    .map((ch) => {
      const c = ch.charCodeAt(0);
      return (c >= 32 && c <= 126) || (c >= 160 && c <= 255) || WIN.has(ch)
        ? ch
        : "?";
    })
    .join("");

// "#f97316", "#abc", "rgb(…)", "hsl(120 85% 58%)" → pdf-lib colour
function toRgb(c) {
  const s = String(c || "")
    .trim()
    .toLowerCase();
  let m;
  if ((m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/))) {
    let h = m[1];
    if (h.length === 3)
      h = h
        .split("")
        .map((x) => x + x)
        .join("");
    return rgb(
      parseInt(h.slice(0, 2), 16) / 255,
      parseInt(h.slice(2, 4), 16) / 255,
      parseInt(h.slice(4, 6), 16) / 255,
    );
  }
  if ((m = s.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/)))
    return rgb(
      Math.min(1, m[1] / 255),
      Math.min(1, m[2] / 255),
      Math.min(1, m[3] / 255),
    );
  if (
    (m = s.match(/^hsla?\(\s*([\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%/))
  ) {
    const h = Number(m[1]) % 360,
      sat = Number(m[2]) / 100,
      l = Number(m[3]) / 100;
    const k = (n) => (n + h / 30) % 12;
    const a = sat * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
    return rgb(f(0), f(8), f(4));
  }
  return rgb(0.976, 0.451, 0.086); // orange
}

const tagOf = (c) =>
  c.wallType && !String(c.wallType).startsWith("__")
    ? String(c.wallType).replace(/·WR$/, "")
    : "";

// drawing helpers in "screen" coordinates (points, origin top-left, y down) on a page H points tall
function painter(page, H, fonts) {
  const font = (bold) => (bold ? fonts.bold : fonts.reg);
  return {
    path(d, o) {
      page.drawSvgPath(d, { x: 0, y: H, ...o });
    },
    rect(x, y, w, h, o) {
      page.drawRectangle({ x, y: H - y - h, width: w, height: h, ...o });
    },
    circle(x, y, r, o) {
      page.drawCircle({ x, y: H - y, size: r, ...o });
    },
    text(s, x, y, size, { bold = false, color = INK, anchor } = {}) {
      const t = safe(s);
      if (!t) return;
      const w = font(bold).widthOfTextAtSize(t, size);
      const x0 = anchor === "end" ? x - w : anchor === "middle" ? x - w / 2 : x;
      page.drawText(t, { x: x0, y: H - y, size, font: font(bold), color });
    },
  };
}

// the takeoff over the sheet. P(latlng) → [x, y] in points (top-left origin)
function drawOverlay(p, { shapes, condById, P }) {
  const lw = U * 0.02;
  const colOf = (s) => toRgb(condById.get(s.conditionId)?.color);
  const d = (list, close) =>
    list
      .map((q, i) => {
        const [x, y] = P(q);
        return `${i ? "L" : "M"}${r2(x)} ${r2(y)}`;
      })
      .join(" ") + (close ? " Z" : "");
  const round = { borderLineCap: LineCapStyle.Round };

  for (const s of shapes)
    if (s.kind === "polygon" && s.latlngs.length > 2) {
      const c = colOf(s);
      p.path(d(s.latlngs, true), {
        color: c,
        opacity: 0.28,
        borderColor: c,
        borderWidth: lw * 0.8,
      });
    }
  for (const s of shapes)
    if (s.kind === "polyline" && s.latlngs.length > 1)
      p.path(d(s.latlngs), {
        borderColor: WHITE,
        borderOpacity: 0.75,
        borderWidth: lw * 2.6,
        ...round,
      });
  for (const s of shapes)
    if (s.kind === "polyline" && s.latlngs.length > 1)
      p.path(d(s.latlngs), {
        borderColor: colOf(s),
        borderWidth: lw * 1.5,
        ...round,
      });
  for (const s of shapes)
    for (const [segs, col] of [
      [s.meta?.windowSegs, toRgb("#22d3ee")],
      [s.meta?.doorSegs, toRgb("#e879f9")],
    ])
      for (const seg of segs || []) {
        if (!seg?.[0] || !seg?.[1]) continue;
        p.path(d(seg), {
          borderColor: col,
          borderOpacity: 0.8,
          borderWidth: lw * 2.8,
        });
      }
  for (const s of shapes)
    if (s.kind === "marker" && s.latlngs[0]) {
      const [x, y] = P(s.latlngs[0]);
      p.circle(x, y, lw * 2.6, {
        color: colOf(s),
        borderColor: WHITE,
        borderWidth: lw * 0.7,
      });
    }
  // wall tag labels (one per stretch of wall, not on top of each other)
  const fs = U * 0.095;
  const placed = [];
  for (const s of shapes) {
    if (s.kind !== "polyline") continue;
    const cond = condById.get(s.conditionId);
    const tag = cond ? tagOf(cond) : "";
    if (!tag) continue;
    let best = null,
      bl = 0;
    for (let i = 0; i + 1 < s.latlngs.length; i++) {
      const a = P(s.latlngs[i]),
        b = P(s.latlngs[i + 1]);
      const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (l > bl) {
        bl = l;
        best = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      }
    }
    if (!best || bl < U * 0.7) continue;
    if (
      placed.some(
        (q) =>
          q.tag === tag && Math.hypot(q.x - best[0], q.y - best[1]) < fs * 9,
      )
    )
      continue;
    placed.push({ tag, x: best[0], y: best[1] });
    const w = p.fonts.bold.widthOfTextAtSize(safe(tag), fs) + fs * 0.9,
      h = fs * 1.35;
    p.rect(best[0] - w / 2, best[1] - h / 2, w, h, {
      color: WHITE,
      borderColor: toRgb(cond.color),
      borderWidth: lw * 0.7,
    });
    p.text(tag, best[0], best[1] + fs * 0.35, fs, {
      bold: true,
      anchor: "middle",
    });
  }
}

// the legend box: measured now, drawn later at (x, y). rows from buildSheetLegends()
function legendBox(
  fonts,
  rows,
  { title = "TAKEOFF LEGEND", sub = "", details = true } = {},
) {
  const fs = 0.115 * U,
    fsS = 0.092 * U,
    fsT = 0.16 * U,
    pad = 0.14 * U,
    sw = 0.15 * U,
    gap = 0.12 * U,
    rg = 0.07 * U,
    maxName = 3.3 * U;
  const TYPE = { linear: "WALLS", area: "AREAS", count: "COUNTS" };
  const wd = (s, size, bold) =>
    (bold ? fonts.bold : fonts.reg).widthOfTextAtSize(safe(s), size);
  const clip = (s, size, bold, maxW) => {
    let t = safe(s);
    if (wd(t, size, bold) <= maxW) return t;
    while (t.length > 1 && wd(`${t}…`, size, bold) > maxW) t = t.slice(0, -1);
    return `${t}…`;
  };
  const items = rows.map((r) => {
    const tag = tagOf(r.cond);
    const lines = details
      ? r.desc.slice(0, 4).concat(r.desc.length > 4 ? ["…"] : [])
      : [];
    return { ...r, label: (tag ? `${tag}  ` : "") + r.cond.name, lines };
  });
  let nameW = 0,
    htW = 0,
    qtyW = 0;
  for (const it of items) {
    nameW = Math.max(nameW, wd(it.label, fs, true));
    qtyW = Math.max(qtyW, wd(it.qty || "", fs, true));
    for (const l of it.lines) nameW = Math.max(nameW, wd(l, fsS, false));
    htW = Math.max(htW, wd(it.height || "", fs, false));
  }
  nameW = Math.min(nameW, maxName);
  const W = Math.max(
    pad * 2 + sw + gap + nameW + (htW ? gap + htW : 0) + gap + qtyW,
    wd(title, fsT, true) + pad * 2,
  );
  const titleH = fsT * 1.35 + (sub ? fsS * 1.5 : 0) + pad * 0.6;
  const grpH = fsS * 1.7;
  let H = pad + titleH;
  let last = null;
  for (const it of items) {
    if (it.type !== last) {
      H += grpH;
      last = it.type;
    }
    H += fs * 1.35 + it.lines.length * fsS * 1.25 + rg;
  }
  H += fsS * 2 + pad * 0.6;

  const draw = (p, x, y) => {
    p.rect(x, y, W, H, { color: WHITE, borderColor: INK, borderWidth: 1 });
    p.text(title, x + pad, y + pad + fsT, fsT, { bold: true });
    if (sub)
      p.text(
        clip(sub, fsS, false, W - pad * 2),
        x + pad,
        y + pad + fsT + fsS * 1.5,
        fsS,
        {
          color: rgb(0.33, 0.33, 0.33),
        },
      );
    let cy = y + pad + titleH;
    let lastT = null;
    const colQ = x + W - pad,
      colH = colQ - qtyW - gap;
    for (const it of items) {
      if (it.type !== lastT) {
        p.rect(x + 1, cy, W - 2, grpH, { color: rgb(0.925, 0.933, 0.945) });
        p.text(TYPE[it.type] || "", x + pad, cy + grpH * 0.72, fsS, {
          bold: true,
          color: rgb(0.27, 0.27, 0.27),
        });
        cy += grpH;
        lastT = it.type;
      }
      p.circle(x + pad + sw / 2, cy + fs * 0.7, sw / 2, {
        color: toRgb(it.cond.color),
        borderColor: rgb(0.2, 0.2, 0.2),
        borderWidth: 0.5,
      });
      const nx = x + pad + sw + gap;
      p.text(clip(it.label, fs, true, nameW), nx, cy + fs, fs, { bold: true });
      if (it.height)
        p.text(it.height, colH, cy + fs, fs, {
          color: rgb(0.27, 0.27, 0.27),
          anchor: "end",
        });
      p.text(it.qty || "", colQ, cy + fs, fs, { bold: true, anchor: "end" });
      it.lines.forEach((l, i) =>
        p.text(
          clip(l, fsS, false, nameW),
          nx,
          cy + fs * 1.35 + (i + 0.9) * fsS * 1.25,
          fsS,
          { color: rgb(0.33, 0.33, 0.33) },
        ),
      );
      cy += fs * 1.35 + it.lines.length * fsS * 1.25 + rg;
    }
    // key
    let kx = x + pad;
    for (const [col, text] of [
      ["#22d3ee", "Window (deducted)"],
      ["#e879f9", "Door (deducted)"],
    ]) {
      p.rect(kx, cy + fsS * 0.35, fsS * 1.8, fsS * 0.7, { color: toRgb(col) });
      p.text(text, kx + fsS * 2.1, cy + fsS * 1.1, fsS, {
        color: rgb(0.27, 0.27, 0.27),
      });
      kx += fsS * 2.1 + wd(text, fsS, false) + gap;
    }
  };
  return { w: W, h: H, draw };
}

/**
 * buildWorkingPdf(sheets) → Uint8Array (the PDF)
 *   sheets: [{ label, jpeg (Uint8Array), sheetPtW, sheetPtH, shapes, condById, P,
 *              legend: { rows, pos: "tl"|"tr"|"bl"|"br"|"side", sub, details } | null }]
 */
export async function buildWorkingPdf(
  sheets,
  { title = "Working drawings" } = {},
) {
  const pdf = await PDFDocument.create();
  pdf.setTitle(safe(title));
  pdf.setCreator("Takeoff");
  const fonts = {
    reg: await pdf.embedFont(StandardFonts.Helvetica),
    bold: await pdf.embedFont(StandardFonts.HelveticaBold),
  };
  const m = 0.2 * U;
  for (const s of sheets) {
    const L = s.legend ? legendBox(fonts, s.legend.rows, s.legend) : null;
    const side = L && s.legend.pos === "side" ? L.w + m * 2 : 0;
    const W = s.sheetPtW + side,
      H = s.sheetPtH;
    const page = pdf.addPage([W, H]);
    const img = await pdf.embedJpg(s.jpeg);
    page.drawImage(img, {
      x: 0,
      y: H - s.sheetPtH,
      width: s.sheetPtW,
      height: s.sheetPtH,
    });
    const p = painter(page, H, fonts);
    p.fonts = fonts;
    drawOverlay(p, s);
    if (L) {
      let x, y;
      if (side) {
        p.path(`M${r2(s.sheetPtW)} 0 L${r2(s.sheetPtW)} ${r2(H)}`, {
          borderColor: INK,
          borderWidth: 1.5,
        });
        x = s.sheetPtW + m;
        y = m;
      } else {
        x = s.legend.pos.endsWith("r") ? s.sheetPtW - L.w - m : m;
        y = s.legend.pos.startsWith("b") ? H - L.h - m : m;
      }
      L.draw(p, x, Math.max(0, y));
    }
  }
  return pdf.save();
}

// show the PDF in the (already open) window with Download / Print / Share. Returns the blob URL (revoke it later).
// show the PDF in the (already open) window with a sheet picker, Download / Print / Share.
// Returns the blob URL (revoke it later).   info: { labels: [sheet names, in page order], skipped: [text] }
export function showPdfWindow(
  w,
  bytes,
  fileName = "working-drawings.pdf",
  { labels = [], skipped = [] } = {},
) {
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/pdf" }),
  );
  const esc = (v) =>
    String(v ?? "").replace(
      /[&<>"]/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
    );
  const js = (v) => JSON.stringify(String(v)).replace(/</g, "\\u003c");
  const pick =
    labels.length > 1
      ? `<select id="pg" title="Go to sheet">${labels
          .map(
            (l, i) => `<option value="${i + 1}">${i + 1}. ${esc(l)}</option>`,
          )
          .join("")}</select>`
      : "";
  w.document.open();
  w.document
    .write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(fileName)}</title><style>
html,body{margin:0;height:100%;background:#111827;font-family:Inter,Arial,sans-serif}
.bar{min-height:46px;display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:6px 14px;color:#fff;font-size:13px;box-sizing:border-box}
.bar b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:40vw}
.n{background:#f97316;border-radius:99px;padding:2px 9px;font-size:12px;font-weight:700}
.warn{color:#fca5a5;font-size:12px}
.bar a,.bar button{background:#f97316;color:#fff;border:0;border-radius:6px;padding:7px 14px;font-weight:600;cursor:pointer;text-decoration:none;font-size:13px;white-space:nowrap}
.bar .g{background:#374151}
select{background:#1f2937;color:#fff;border:1px solid #374151;border-radius:6px;padding:6px 8px;font-size:12px;max-width:260px}
iframe{border:0;width:100%;height:calc(100% - 46px);display:block;background:#525659}
</style></head><body>
<div class="bar"><b>${esc(fileName)}</b><span class="n">${labels.length || 1} sheet${labels.length === 1 ? "" : "s"}</span>${pick}
${skipped.length ? `<span class="warn">Left out: ${esc(skipped.join(" · "))}</span>` : ""}
<span style="flex:1"></span>
<button id="sh" class="g" hidden>Share</button>
<button class="g" onclick="document.getElementById('f').contentWindow.print()">Print</button>
<a href="${url}" download="${esc(fileName)}">Download PDF</a></div>
<iframe id="f" src="${url}"></iframe>
<script>
var U=${js(url)},F=document.getElementById("f"),S=document.getElementById("pg");
if(S)S.onchange=function(){var v=this.value;F.src="about:blank";setTimeout(function(){F.src=U+"#page="+v;},30);};
(async()=>{try{const b=await (await fetch(U)).blob();const f=new File([b],${js(fileName)},{type:"application/pdf"});
if(navigator.canShare&&navigator.canShare({files:[f]})){const e=document.getElementById("sh");e.hidden=false;
e.onclick=()=>navigator.share({files:[f],title:${js(fileName)}}).catch(()=>{});}}catch(e){}})();
</script>
</body></html>`);
  w.document.close();
  w.focus();
  return url;
}
